import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../config/firebase';
import { VehicleTransfer, VehicleTransferStatus } from '../../../../shared/types/vehicle-transfer.types';
import useVehicleTransfer from '../../wrappers/use-vehicle-transfer';
import useVehicle from '../../wrappers/use-vehicle';
import { useTranslation } from 'react-i18next';
import { transferQueueStyles } from './TransferQueue.styles';

const statusConfig: Record<VehicleTransferStatus, { color: 'warning' | 'success' | 'error' | 'info' }> = {
    pending: { color: 'warning' },
    approved: { color: 'success' },
    rejected: { color: 'error' },
    infoRequested: { color: 'info' },
};

interface EnrichedTransfer extends VehicleTransfer {
    vehicleModel?: string;
    fromUserName?: string;
    toUserName?: string;
}

const TransferQueue: React.FC = () => {
    const { t } = useTranslation();
    const [filter, setFilter] = useState<'all' | 'pending'>('pending');
    const [transfers, setTransfers] = useState<EnrichedTransfer[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { list: listTransfers } = useVehicleTransfer();
    const { get: getVehicle } = useVehicle();

    useEffect(() => {
        const fetchTransfers = async () => {
            const raw = await listTransfers();

            const uniqueVins = [...new Set(raw.map((t) => t.vehicleUid))];
            const vehicleModelMap: Record<string, string> = {};
            await Promise.all(uniqueVins.map(async (vin) => {
                const v = await getVehicle(vin);
                if (v) vehicleModelMap[vin] = v.model;
            }));

            const userUids = new Set<string>();
            raw.forEach((t) => {
                if (t.fromUserUid) userUids.add(t.fromUserUid);
                if (t.toUserUid) userUids.add(t.toUserUid);
            });
            const userNameMap: Record<string, string> = {};
            await Promise.all([...userUids].map(async (uid) => {
                const snap = await getDoc(doc(firestore, 'user', uid));
                if (snap.exists()) userNameMap[uid] = snap.data().name ?? '';
            }));

            setTransfers(raw.map((t) => ({
                ...t,
                vehicleModel: vehicleModelMap[t.vehicleUid],
                fromUserName: t.fromUserUid ? userNameMap[t.fromUserUid] : undefined,
                toUserName: t.toUserUid ? userNameMap[t.toUserUid] : undefined,
            })));
            setLoading(false);
        };

        fetchTransfers();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredTransfers = transfers.filter((t) => {
        if (filter === 'all') return true;
        return t.status === 'pending' || t.status === 'infoRequested';
    });

    return (
        <Box sx={transferQueueStyles.root}>
            <Box sx={transferQueueStyles.header}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>{t('transferQueue.title')}</Typography>
                    <Typography color="text.secondary" mt={0.5}>{t('transferQueue.subtitle')}</Typography>
                </Box>
                <ButtonGroup size="small" variant="outlined">
                    <Button onClick={() => setFilter('pending')} variant={filter === 'pending' ? 'contained' : 'outlined'}>{t('transferQueue.filterPending')}</Button>
                    <Button onClick={() => setFilter('all')} variant={filter === 'all' ? 'contained' : 'outlined'}>{t('transferQueue.filterAll')}</Button>
                </ButtonGroup>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={transferQueueStyles.tableContainer}>
                <Table>
                    <TableHead>
                        <TableRow sx={transferQueueStyles.tableHeaderRow}>
                            <TableCell sx={transferQueueStyles.tableHeaderCell}>{t('transferQueue.colMachine')}</TableCell>
                            <TableCell sx={transferQueueStyles.tableHeaderCell}>{t('transferQueue.colFromOwner')}</TableCell>
                            <TableCell sx={transferQueueStyles.tableHeaderCell}>{t('transferQueue.colToOwner')}</TableCell>
                            <TableCell sx={transferQueueStyles.tableHeaderCell}>{t('transferQueue.colStatus')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={transferQueueStyles.loadingCell}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {filteredTransfers.map((transfer) => {
                                    const status = statusConfig[transfer.status ?? 'pending'];
                                    return (
                                        <TableRow
                                            key={transfer.id}
                                            hover
                                            onClick={() => navigate(`/app/admin/transfers/${transfer.id}`)}
                                            sx={transferQueueStyles.clickableRow}
                                        >
                                            <TableCell>
                                                {transfer.vehicleModel && (
                                                    <Typography variant="body2" fontWeight={600}>{transfer.vehicleModel}</Typography>
                                                )}
                                                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                                    {transfer.vehicleUid}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={transferQueueStyles.userCellRow}>
                                                    {transfer.fromUserName && (
                                                        <Typography variant="body2" fontWeight={500}>{transfer.fromUserName}</Typography>
                                                    )}
                                                    {transfer.fromUserUid === transfer.createdBy && (
                                                        <Chip label={t('transferQueue.requesterChip')} size="small" color="primary" variant="outlined" />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={transferQueueStyles.userCellRow}>
                                                    {transfer.toUserName && (
                                                        <Typography variant="body2" fontWeight={500}>{transfer.toUserName}</Typography>
                                                    )}
                                                    {transfer.toUserUid === transfer.createdBy && (
                                                        <Chip label={t('transferQueue.requesterChip')} size="small" color="primary" variant="outlined" />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={t(`vehicleTransferStatus.${transfer.status ?? 'pending'}`)} color={status.color} size="small" />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {filteredTransfers.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={transferQueueStyles.emptyCell}>
                                            {t('transferQueue.empty')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default TransferQueue;
